import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DiscussionDetail = () => {
  const { id } = useParams();
  const [discussion, setDiscussion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    fetchDiscussion();
  }, [id]);

  const fetchDiscussion = async () => {
    try {
      const response = await axios.get(`${API}/discussions/${id}`);
      setDiscussion(response.data);
    } catch (error) {
      console.error('Failed to fetch discussion:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      await axios.post(
        `${API}/discussions/${id}/reply?content=${encodeURIComponent(replyContent)}`,
        {},
        { withCredentials: true }
      );
      toast.success('Reply posted!');
      setReplyContent('');
      fetchDiscussion();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to reply');
        const redirectUrl = `${window.location.origin}/auth-callback`;
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      } else {
        toast.error('Failed to post reply');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="loading">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }

  if (!discussion) return null;

  return (
    <div className="min-h-screen bg-black" data-testid="discussion-detail-page">
      <Navbar />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <Link to="/community" className="text-red-500 hover:text-red-400 mb-6 inline-block" data-testid="back-link">
            ← Back to Community
          </Link>

          {/* Original Post */}
          <div className="glass-effect p-8 rounded-xl mb-8" data-testid="original-post">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-red-500 font-semibold" data-testid="category">{discussion.category}</span>
              <span className="text-sm text-gray-400" data-testid="date">{new Date(discussion.created_at).toLocaleDateString()}</span>
            </div>
            
            <h1 className="text-3xl font-bold mb-6" data-testid="title">{discussion.title}</h1>
            
            <div className="flex items-start space-x-4 mb-6">
              {discussion.user?.picture ? (
                <img src={discussion.user.picture} alt={discussion.user.name} className="w-12 h-12 rounded-full" data-testid="author-avatar" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center font-bold" data-testid="author-avatar-placeholder">
                  {discussion.user?.name?.charAt(0) || 'A'}
                </div>
              )}
              <div>
                <p className="font-semibold" data-testid="author-name">{discussion.user?.name || 'Anonymous'}</p>
                <p className="text-sm text-gray-400">Original Poster</p>
              </div>
            </div>

            <p className="text-gray-300 whitespace-pre-wrap leading-relaxed" data-testid="content">{discussion.content}</p>
          </div>

          {/* Replies Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2" data-testid="replies-title">
              <MessageSquare size={24} className="text-red-500" />
              <span>{discussion.replies?.length || 0} Replies</span>
            </h2>

            {discussion.replies && discussion.replies.length > 0 ? (
              <div className="space-y-4" data-testid="replies-list">
                {discussion.replies.map(reply => (
                  <div key={reply.id} className="glass-effect p-6 rounded-xl" data-testid={`reply-${reply.id}`}>
                    <div className="flex items-start space-x-4">
                      {reply.user?.picture ? (
                        <img src={reply.user.picture} alt={reply.user.name} className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center font-bold text-sm">
                          {reply.user?.name?.charAt(0) || 'A'}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <p className="font-semibold">{reply.user?.name || 'Anonymous'}</p>
                          <span className="text-xs text-gray-500">{new Date(reply.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-300 whitespace-pre-wrap">{reply.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8" data-testid="no-replies">No replies yet. Be the first to comment!</p>
            )}
          </div>

          {/* Reply Form */}
          <div className="glass-effect p-6 rounded-xl" data-testid="reply-form">
            <h3 className="text-xl font-bold mb-4">Post a Reply</h3>
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Share your thoughts..."
              rows="4"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500 resize-none mb-4"
              data-testid="reply-input"
            />
            <button
              onClick={handleReply}
              className="btn-primary flex items-center space-x-2"
              data-testid="submit-reply-btn"
            >
              <Send size={18} />
              <span>Post Reply</span>
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DiscussionDetail;